// Data-source attribution copy for the dashboard's right-hand panel.
//
// Source: hvsourcedata/Detailed_template_v3.csv, the "Data Source" column. Copy
// is keyed by CATEGORY (the "Category" dropdown) and GEOGRAPHIC SCOPE — the same
// two axes the CSV uses. This sits below the narrative and cites where the
// numbers in the panel came from and how they were derived.
//
// Sibling of metricNarratives.ts, which owns the same CSV's "Narrative/Insights"
// column. The split follows the panel: the narrative is per-GROUP and reads live
// values, this is per-CATEGORY and is entirely static.
//
// Scope only matters where the CSV genuinely differs:
//   - home-visitors  three texts differing only in the geography word, so each
//                    scope names the level the counts were aggregated over
//   - female / tenure / age / education   one text; the CSV lists it under
//                    National and State only, because these are state-only
//                    metrics that never surface at county scope
//   - capacity / earnings / graduates / households   one text at every scope
//
// Changed in v3: the per-scope model-developer rosters (29/27 national, 26/24
// state, 18/16 county) collapsed into a single "28 participating models" line
// that defers to the technical appendix, and "Workforce shortage" (`capacity`)
// gained the attribution v2 left blank. The IAFSP, IPEDS, BLS and ACS texts are
// unchanged from v2.
//
// PLACEHOLDERS: "(N = COUNT)", "[list models]" and "[LIST CODES]" are unresolved
// in the source CSV and render literally in the panel. The CSV's Notes column
// tracks them ("Nathan please check data source- add IAFSP total count").
// ---- Home visitors and supervisors (per-scope aggregation level) ----
// The three differ only in the geography word; kept as separate constants rather
// than interpolated so each stays a literal transcription of its CSV row.
const HOME_VISITORS_NATIONAL = 'Estimated count of home visitors and supervisors in 2025 aggregated at the national level ' +
    'using data from 29 participating models. See technical appendix for full methodology.';
const HOME_VISITORS_STATE = 'Estimated count of home visitors and supervisors in 2025 aggregated at the state level ' +
    'using data from 29 participating models. See technical appendix for full methodology.';
const HOME_VISITORS_COUNTY = 'Estimated count of home visitors and supervisors in 2025 aggregated at the county level ' +
    'using data from 29 participating models. See technical appendix for full methodology.';
// ---- Workforce shortage ----
// New in v3; identical at every scope. The doubled "with with" is a typo in the
// source CSV, reproduced verbatim so this file stays a faithful transcription.
const CAPACITY = 'This estimate is calculated by dividing the estimated count of households under 200% ' +
    'of the federal poverty level with with at least one child (under 3 or under 6 and not ' +
    'yet in kindergarten) or a pregnant woman by 20 (an average caseload size), multiplying ' +
    'by 25% (a target goal of the share of households served), and then subtracting the ' +
    'number of home visitors currently working in the area. See technical appendix for ' +
    'full methodology.';
// ---- IAFSP-sourced demographics (state-only metrics) ----
// Shared lead-in and trailing coverage note, so a copy edit to the boilerplate
// cannot land in one of the four and miss the others.
const IAFSP_LEAD = 'Institute for the Advancement of Family Support Professionals (N = 29,953). ';
const IAFSP_COVERAGE = ' Data available for each of the 50 States and the District of Columbia and includes 6 specified home visiting models, ' +
    'including Early Head Start - Home Based Option, Healthy Families America, Home Instruction for Parents of Preschool Youngsters, ' +
    'Nurse-Family Partnership, Parents as Teachers, and SafeCare.';
const FEMALE = IAFSP_LEAD +
    'An estimate is calculated by dividing the count of home visitors that are female by ' +
    'the total number of home visitors with an IAFSP user account created between March ' +
    '2021 and June 30, 2026, and valid data entered on this variable.' +
    IAFSP_COVERAGE;
const TENURE = IAFSP_LEAD +
    'Categorical estimates are calculated by dividing the count of home visitors within a ' +
    'certain range of work experience by the total number of home visitors with an IAFSP ' +
    'user account created between March 2021 and June 30, 2026, and a valid number of years ' +
    'of experience entered. An average is calculated based on all valid years entered.' +
    IAFSP_COVERAGE;
const AGE = IAFSP_LEAD +
    'Categorical estimates are calculated by dividing the count of home visitors in an age ' +
    'category by the total number of home visitors with an IAFSP user account created ' +
    'between March 2021 and June 30, 2026, and a valid age entered. An average is ' +
    'calculated based on all valid ages entered.' +
    IAFSP_COVERAGE;
const EDUCATION = IAFSP_LEAD +
    'Categorical estimates are calculated by dividing the count of home visitors in an ' +
    'educational category by the total number of home visitors with an IAFSP user account ' +
    'created between March 2021 and June 30, 2026, and a valid education level entered.' +
    IAFSP_COVERAGE;
// ---- Adjacent fields & households (identical at every scope) ----
const GRADUATES = '2026 US Department of Education Integrated Postsecondary Education Data System (IPEDS) ' +
    'Completion data. Counts the number of sub-bachelor’s degree credential completers at ' +
    'Title IV institutions in the 2024 to 2025 academic year in 17 fields of study related ' +
    'to home visiting (as recorded by the 6-digit Classifications of Instructional Programs ' +
    'codes) [LIST CODES]: 1312: Teacher Education and Professional Development, Specific ' +
    'Levels and Methods (Early Childhood Education); 5138: Registered Nursing, Nursing ' +
    'Admin, Nursing Research, Clinical Nursing and 5116: Nursing (composite proxy for ' +
    'nursing); 4407: Social Work (Social Work); 5122: Public Health (Community Health ' +
    'Work). Completer accounts are assigned to geographies based on the state and county of ' +
    'the institution offering the program.';
const EARNINGS = '2026 Bureau of Labor Statistics data. Includes the annual earnings and location ' +
    'quotient data of labor market-level records in four occupations in adjacent fields: ' +
    'Early childhood education composite proxy composed of: 25-2011: Preschool Teachers, ' +
    'Except Special Education, 25-2012: Kindergarten Teachers, Except Special Education, ' +
    '25-2021: Elementary School Teachers, Except Special Education, 25-2051: Special ' +
    'Education Teachers, Preschool, 25-2052: Special Education Teachers, Kindergarten and ' +
    'Elementary School; 29-1141: Registered Nurses (proxy for nurses); 21-1021: Child, ' +
    'Family, and School Social Workers (proxy for social workers); 21-1094: Community ' +
    'Health Workers (proxy for community health workers).';
const HOUSEHOLDS = 'American Community Survey, 2000–2024 5-year data file. Weighted population estimates ' +
    'are calculated for each state and county with sufficient population sizes.';
const DATA_SOURCES = {
    'home-visitors': {
        national: HOME_VISITORS_NATIONAL,
        state: HOME_VISITORS_STATE,
        county: HOME_VISITORS_COUNTY
    },
    capacity: CAPACITY,
    female: { national: FEMALE, state: FEMALE },
    tenure: { national: TENURE, state: TENURE },
    age: { national: AGE, state: AGE },
    education: { national: EDUCATION, state: EDUCATION },
    earnings: EARNINGS,
    graduates: GRADUATES,
    households: HOUSEHOLDS
};
/**
 * The data-source attribution for a category at a geographic scope. Returns null
 * when the CSV supplies no copy for that pairing, so the caller renders nothing
 * rather than an empty heading.
 */
export function getDataSource(categoryId, scope) {
    var _a;
    const entry = categoryId ? DATA_SOURCES[categoryId] : undefined;
    if (entry === undefined) {
        return null;
    }
    return (_a = (typeof entry === 'string' ? entry : entry[scope])) !== null && _a !== void 0 ? _a : null;
}
